import type { CookieRef } from "#app"

import { uuidv7 } from "uuidv7"

import { GlobalComposable } from "~/classes/composable/GlobalComposable"
import { PropertyId, PropertySchema, PropertyStatus } from "~/classes/entity"
import { FileUserRole } from "~/classes/file/properties/FileUserRole"
import { StaticFile } from "~/classes/file/StaticFile"
import { sendSelfInfoRequest } from "~/classes/handlers/user/info"
import { sendSelfUpdateRequests } from "~/classes/handlers/user/update"
import { InterfaceUser } from "~/classes/users/InterfaceUser"
import { defaultUserInterfaceSettings } from "~/utils/flatbuffers/user"

class Self extends GlobalComposable<{
  user?: InterfaceUser
  home?: StaticFile
  archive?: StaticFile
  conversations?: Record<string, StaticConversation>
  sidebar?: Record<string, UserSidebarItem>
  /**
   * this is set when the user successfully connects anonymously. The user
   * is not allowed to initiate a session fully offline as an anonymous user.
   */
  anonymous?: boolean
}> {
  clientTokenCookie: CookieRef<string | null> = ref(null)

  override defaultState = () => ({})
  override use = () => {
    this.state = useState(`use-${this.name}`, this.defaultState)
    this.clientTokenCookie = useCookie("client_token")
    return this
  }

  generateAnonId = () => {
    return generateWorkerRequestId()
  }

  clearUserData = () => {
    this.clientTokenCookie.value = null
    this.state.value = {}
  }

  /**
   * This technically uses the cache state but is more logically placed here.
   * This checks for whether we are an anonymous user either offline or online.
   */
  readonly isAnonymous = () => {
    const { cacheState } = useCache()
    return isDefined(cacheState.value.anonId)
  }

  /**
   * This technically uses the cache state but is more logically placed here
   * This checks for whether we are a logged in user either offline or online.
   */
  readonly isAuthenticated = computed(() => {
    const { cacheState } = useCache()
    return isDefined(cacheState.value.userId)
  })

  /**
   * On desktop, we have a different cookie jar. Eventually, we might write a command to request
   * the user's identity from the cookie jar but for now we just use the existence of a stored
   * user id. This is okay because I think the websocket code on desktop can detect the
   * difference between network and cookie issues, whereas wasm can't seemingly do so.
   */
  hasStoredUserCredentials = () => {
    if (isDesktop())
      return false // FIXME: what is the login negotiation on desktop?
    return !!this.clientTokenCookie.value
  }

  _currentFolderRole = () => {
    const { currentRootFolder } = useNavigation()
    const { currentSharedFolderRole } = useSharedFolders()

    if (this.state.value.home?.id === currentRootFolder.value?.asStatic().rootFolder())
      return FileUserRole.newOwner()
    else if (this.state.value.archive?.id === currentRootFolder.value?.asStatic().rootFolder())
      return FileUserRole.newOwner()
    else if (isDefined(currentSharedFolderRole.value))
      return currentSharedFolderRole.value
  }

  currentFolderRole = computed(this._currentFolderRole)

  requestAllSelfDetails = () => {
    const { requestSelfSharedFolders } = useSharedFolders()
    const { requestSelfSidebar } = useSidebar()
    const { requestSelfConversations } = useConversations()
    this.requestSelfInfo()
    requestSelfConversations()
    requestSelfSidebar()
    requestSelfSharedFolders()
  }

  /**
   * If we receive a connection event that we connected to the cache, and the user id is
   * different than our existing one, we need to request self info for that newly
   * connected user. This could be due to an offline connection, etc.
   */
  onConnectAuthStatus = (_datagram: DidConnectAuthStatusDatagram) => {
    this.debug("Received authenticated connection for a new user")
  }

  requestSelfInfo = () => {
    const request = {
      requestId: generateWorkerRequestId(),
      offline: false,
    }
    sendSelfInfoRequest(request)
    return request
  }

  onConnectAnonStatus = (_datagram: DidConnectAnonStatusDatagram) => {
  // TODO: is there anything we should do here? get conversations?
  }

  /**
   * This function is very important. Either cached or via the server, this is
   * the first confirmation that we may proceed with the application from loading
   * state, usually. So we want to set up anything in the UI that is important,
   * and then ensure that we have the user info in the right place, and set the
   * `myself` value at the end.
   */
  onSelfInfoResponse = (response: SelfInfoResponseDatagram) => {
    const { subscribeToFolderUpdates } = useFileSystem()
    const { logOut } = useAuthentication()
    const { home, archive, user, error, offline } = response
    const { loadViewModeSettingsFromUser } = useViewMode()

    if (error && !offline) {
    // for non-offline errors, then we need to log out
      this.error("Self info errored", error)
      return logOut()
    }

    if (error || !home || !archive || !user)
      return this.info("Cached self info errored (likely first time logging in)")

    if (!offline) {
      const updates = [home.id, archive.id]
      subscribeToFolderUpdates(updates)
    }

    this.state.value.home = new StaticFile(home)
    this.state.value.archive = new StaticFile(archive)
    this.state.value.user = new InterfaceUser(user)

    loadViewModeSettingsFromUser(this.state.value.user)
  }

  onSelfUpdateResponse = (response: SelfUpdateResponseDatagram) => {
    if (isDefined(response.error) || !isDefined(response.user))
      return
    if (!isDefined(this.state.value.user))
      throw new Error("Not logged in")

    const iUser = new InterfaceUser(response.user)
    this.state.value.user = this.state.value.user.mergeWithNewer(iUser)
  }

  /**
   * This is a readonly ref for user interface settings for a given user. Note that
   * for anonymous users, this will always show the default settings. Instead, you must
   * persist your own managed state variable to the user interface settings if the user
   * is logged in.
   */
  readonly userInterfaceSettings = computed(() => this.state.value.user?.interfaceSettings() ?? defaultUserInterfaceSettings())

  persistUserInterfaceSettings = (changes?: Partial<UserInterfaceSettings>) => {
    if (!isDefined(this.state.value.user))
      throw new Error("Cannot update settings without a user")
    const userInterfaceSettings = {
      ...this.userInterfaceSettings.value,
      ...changes,
    }
    if (deepEqual(userInterfaceSettings, this.state.value.user.interfaceSettings()))
      return console.info("Skipping persisting user settings, no changes", changes)

    const property: UserProperty = {
      propertyId: PropertyId.USER_INTERFACE_SETTINGS,
      id: this.state.value.user.id,
      schema: PropertySchema.UserInterfaceSettingsValue,
      version: 0, // derived
      status: PropertyStatus.StatusPending,
      value: { type: PropertySchema.UserInterfaceSettingsValue, userInterfaceSettings },
      timestamp: uuidv7(),
    }
    // update our own user settings first, in case multiple requests are sent before
    // one of them returns
    this.state.value.user.addProperty(property)

    const user = InterfaceUser.blank(this.state.value.user.id)
    user.addProperty(property)
    sendSelfUpdateRequests([{
      requestId: generateWorkerRequestId(),
      offline: false,
      user: user.inner(),
    }])
  }

  override onCreate = () => {
    const { addInterfaceEventListener } = useEvents()
    addInterfaceEventListener("selfInfoResponse", this.onSelfInfoResponse)
    addInterfaceEventListener("selfUpdateResponse", this.onSelfUpdateResponse)
    addInterfaceEventListener("didConnectAuthStatus", this.onConnectAuthStatus)
    addInterfaceEventListener("didConnectAnonStatus", this.onConnectAnonStatus)
  }

  override onDestroy = () => {
    const { removeInterfaceEventListener } = useEvents()
    removeInterfaceEventListener("selfInfoResponse", this.onSelfInfoResponse)
    removeInterfaceEventListener("selfUpdateResponse", this.onSelfUpdateResponse)
    removeInterfaceEventListener("didConnectAuthStatus", this.onConnectAuthStatus)
    removeInterfaceEventListener("didConnectAnonStatus", this.onConnectAnonStatus)
  }
}

const self = new Self("self", theme.colors.purple.hex)
export default function () {
  return self.use()
}
